import {
  calendarInputToIso,
  toLatinDigits,
  type CalendarSystem,
} from './calendar';

export interface SearchNormalizationOptions {
  /** Converts date-like query tokens from the selected display calendar to canonical ISO variants. */
  calendar?: CalendarSystem;
}

const localizedDatePattern = /(?<!\d)\d{3,4}[\/.-]\d{1,2}[\/.-]\d{1,2}(?!\d)/gu;

function normalizeCurrencyTerms(value: string): string {
  return value
    .replace(/\$/gu, ' usd ')
    .replace(/\u20ac/gu, ' eur ')
    .replace(/\u00a3/gu, ' gbp ')
    .replace(/\ufdfc/gu, ' irr ')
    .replace(/\u0631\u06cc\u0627\u0644/gu, ' irr ')
    .replace(/\u062a\u0648\u0645\u0627\u0646/gu, ' irt ')
    .replace(/\u062f\u0644\u0627\u0631/gu, ' usd ')
    .replace(/\brials?\b/gu, ' irr ')
    .replace(/\btomans?\b/gu, ' irt ');
}

/**
 * Canonical text folding for UI indexes and query terms. This function is display-only: identifiers
 * sent to APIs remain exact, while comparisons tolerate the scripts and keyboards Varza supports.
 */
export function normalizeSearchText(value: string | null | undefined): string {
  if (!value) return '';

  const withCurrencyCodes = normalizeCurrencyTerms(toLatinDigits(value))
    .normalize('NFKC')
    .toLocaleLowerCase('en-US')
    .replace(/[\u064a\u0649]/gu, '\u06cc')
    .replace(/\u0643/gu, '\u06a9')
    .replace(/[\u0623\u0625\u0671]/gu, '\u0627')
    .replace(/\u0624/gu, '\u0648')
    .replace(/\u0626/gu, '\u06cc')
    .replace(/[\u06c0\u06c2]/gu, '\u0647')
    .replace(/\u0640/gu, '')
    .replace(/\p{M}+/gu, '')
    .replace(/[\u200b-\u200f\u202a-\u202e\u2066-\u2069]/gu, ' ')
    .replace(/(?<=\d)[,\u066c](?=\d{3}(?:\D|$))/gu, '')
    .replace(/\u066b/gu, '.');

  return normalizeCurrencyTerms(withCurrencyCodes)
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim()
    .replace(/\s+/gu, ' ');
}

/** Returns the literal folded query and, when possible, its canonical ISO-date equivalent. */
export function normalizedSearchVariants(
  value: string | null | undefined,
  options: SearchNormalizationOptions = {},
): readonly string[] {
  const normalized = normalizeSearchText(value);
  if (!value || options.calendar === undefined) return normalized ? [normalized] : [''];

  const latin = toLatinDigits(value);
  const canonicalDates = latin.replace(
    localizedDatePattern,
    (candidate) => calendarInputToIso(candidate, options.calendar!) ?? candidate,
  );
  const canonical = normalizeSearchText(canonicalDates);
  return canonical && canonical !== normalized ? [normalized, canonical] : [normalized];
}

export function matchesLocalizedSearch(
  searchableValue: string | null | undefined,
  query: string | null | undefined,
  options: SearchNormalizationOptions = {},
): boolean {
  const needles = normalizedSearchVariants(query, options).filter(Boolean);
  if (needles.length === 0) return true;
  const haystacks = normalizedSearchVariants(searchableValue, options);
  return needles.some((needle) => {
    const terms = needle.split(' ');
    return haystacks.some((haystack) => terms.every((term) => haystack.includes(term)));
  });
}
